import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, brl } from "@/api";
import { useAuth } from "@/AuthContext";
import { Money, HideMoneyButton, useHiddenValues } from "@/contexts/HiddenValues";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Wrench, ShoppingCart, Package, Filter, X as XIcon, Check as CheckIcon, TrendingDown, Download } from "lucide-react";
import { FinanceiroExportDialog } from "@/components/ReportExportDialogs";

// Helpers para converter a data LOCAL (YYYY-MM-DD escolhido pelo usuário ou
// computado por um preset) em strings ISO com offset UTC, garantindo que o
// filtro respeite o início/fim do dia no fuso local do navegador. Isso é
// crucial: o backend armazena `created_at` em UTC, mas o usuário pensa em
// dias do calendário local.
function localDayStartISO(ymd) {
  // ymd = "YYYY-MM-DD"; produz ISO UTC equivalente a 00:00 LOCAL desse dia.
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}
function localDayEndISO(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

// Presets de período. Cada preset retorna { start, end } como "YYYY-MM-DD"
// usando a DATA DE HOJE no momento do clique (regra requerida pelo usuário).
const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const PRESETS = {
  month: {
    label: "Este mês",
    compute: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: fmt(start), end: fmt(now) };
    },
  },
  week: {
    label: "Esta semana",
    compute: () => {
      const now = new Date();
      // Semana começa no domingo (padrão BR de calendário)
      const dow = now.getDay();
      const start = new Date(now);
      start.setDate(now.getDate() - dow);
      return { start: fmt(start), end: fmt(now) };
    },
  },
  d15: {
    label: "Últimos 15 dias",
    compute: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 14); // 15 dias incluindo hoje
      return { start: fmt(start), end: fmt(now) };
    },
  },
  year: {
    label: "Anual",
    compute: () => {
      const now = new Date();
      return { start: `${now.getFullYear()}-01-01`, end: fmt(now) };
    },
  },
  all: {
    label: "Total",
    compute: () => ({ start: "", end: "" }), // sem filtro = todo o período
  },
  custom: {
    label: "Personalizado",
    compute: null, // o usuário escolhe manualmente
  },
};

const PRESET_KEY = "gestaoexpert:financeiro:preset";

export default function Financeiro() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canFilterSeller = user && ["master", "owner", "gerente"].includes(user.role);
  const [exportOpen, setExportOpen] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [preset, setPreset] = useState(() => {
    try { return localStorage.getItem(PRESET_KEY) || "month"; } catch { return "month"; }
  });
  const [data, setData] = useState({
    total_sold: 0, total_cost: 0, net_profit: 0,
    sales_total: 0, sales_profit: 0,
    paid_total: 0, paid_profit: 0,
    service_total: 0, service_profit: 0,
    sales: [], paid_orders: [], services: [],
    commissions_paid_total: 0, commissions_paid: [],
  });
  // ── Filtro de categorias de produtos (multi-seleção) ─────────────────
  // Quando uma ou mais estão marcadas, o backend só conta os itens que
  // pertencem àquelas categorias. OS (Assistência) é excluída do total.
  const [categories, setCategories] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]); // array de category ids
  const [catFilterOpen, setCatFilterOpen] = useState(false);
  // ── Filtro de Lead (origem do cliente) — multi-seleção ────────────────
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]); // array de lead.id; vazio = Todos
  // ── Filtro de Vendedor (Ajuste 3) — só para ADM/Gerente/Master ─────
  // IMPORTANTE: precisa ser declarado ANTES do useCallback de applyPreset,
  // que captura selectedSellers como dependência. Caso contrário, TDZ crash.
  const [sellers, setSellers] = useState([]);
  const [selectedSellers, setSelectedSellers] = useState([]);

  // Faz a chamada usando os args explícitos (evita stale state em closures).
  // Converte YYYY-MM-DD → ISO UTC do início/fim de dia LOCAL para evitar
  // que vendas feitas perto da meia-noite caiam fora do período.
  const loadWith = useCallback(async (s, e, cats, leadIds, sellerIds) => {
    const params = {};
    if (s) params.start = localDayStartISO(s);
    if (e) params.end = localDayEndISO(e);
    if (cats && cats.length > 0) params.category_ids = cats;
    if (leadIds && leadIds.length > 0) params.lead_ids = leadIds;
    if (sellerIds && sellerIds.length > 0) params.seller_ids = sellerIds;
    const r = await api.get("/financial/summary", { params, paramsSerializer: { indexes: null } });
    setData(r.data);
  }, []);

  const applyPreset = useCallback((key) => {
    setPreset(key);
    try { localStorage.setItem(PRESET_KEY, key); } catch { /* ignore */ }
    const p = PRESETS[key];
    if (!p || !p.compute) return; // "custom" — não recomputa automaticamente
    const { start: s, end: e } = p.compute();
    setStart(s);
    setEnd(e);
    loadWith(s, e, selectedCats, selectedLeads, selectedSellers);
  }, [loadWith, selectedCats, selectedLeads, selectedSellers]);

  // Bootstrap: aplica o preset salvo (ou "month" default) ao montar.
  // Importante: a regra do usuário diz "sempre considerar a data atual no momento
  // em que a aba for aberta" — por isso recalculamos as datas a cada montagem.
  useEffect(() => {
    // Carrega categorias e leads para o filtro
    api.get("/categories").then(r => setCategories(r.data || [])).catch(() => {});
    api.get("/sale-leads").then(r => setLeads(r.data || [])).catch(() => {});
    if (preset === "custom") {
      // sem preset, abre com lista completa
      loadWith("", "", selectedCats, selectedLeads, selectedSellers);
    } else {
      applyPreset(preset);
    }
    // eslint-disable-next-line
  }, []);

  const applyCustom = () => {
    setPreset("custom");
    try { localStorage.setItem(PRESET_KEY, "custom"); } catch { /* ignore */ }
    loadWith(start, end, selectedCats, selectedLeads, selectedSellers);
  };

  const clearCustom = () => {
    setStart("");
    setEnd("");
    applyPreset("all");
  };

  // ─── Helpers do filtro de categorias ──────────────────────────────────
  const toggleCategory = (cid) => {
    setSelectedCats(prev => {
      const next = prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid];
      loadWith(start, end, next, selectedLeads, selectedSellers);
      return next;
    });
  };
  const selectAllCategories = () => {
    const all = categories.map(c => c.id);
    setSelectedCats(all);
    loadWith(start, end, all, selectedLeads, selectedSellers);
  };
  const clearCategoryFilter = () => {
    setSelectedCats([]);
    loadWith(start, end, [], selectedLeads, selectedSellers);
  };

  const toggleLead = (lid) => {
    setSelectedLeads(prev => {
      const next = prev.includes(lid) ? prev.filter(x => x !== lid) : [...prev, lid];
      loadWith(start, end, selectedCats, next, selectedSellers);
      return next;
    });
  };
  const clearLeads = () => {
    setSelectedLeads([]);
    loadWith(start, end, selectedCats, [], selectedSellers);
  };

  // ─── Filtro de Vendedor (Ajuste 3) — só para ADM/Gerente/Master ─────
  useEffect(() => {
    if (!canFilterSeller) return;
    api.get("/sellers").then(r => setSellers(r.data || [])).catch(() => { /* ignore */ });
  }, [canFilterSeller]);
  const sellerById = useMemo(() => Object.fromEntries(sellers.map(s => [s.id, s])), [sellers]);
  const setSellerFilter = (id) => {
    // "all" limpa; outro id define seleção única.
    const next = id === "all" ? [] : [id];
    setSelectedSellers(next);
    loadWith(start, end, selectedCats, selectedLeads, next);
  };
  const sellerLabel = (item) => {
    if (item?.created_by_user_id && sellerById[item.created_by_user_id]) {
      return sellerById[item.created_by_user_id].label;
    }
    return item?.created_by || "—";
  };

  const presetKeys = ["month", "week", "d15", "year", "all", "custom"];

  // Resumo de itens para a coluna "Produto" das tabelas Vendas / Pedidos pagos.
  // Lista 1-2 itens; se houver mais, agrega "+N". Quantidade prefixada quando >1.
  const summarizeItems = (items) => {
    if (!items || items.length === 0) return "—";
    const parts = items.map(it => {
      const name = (it.name || "Item").trim();
      const qty = Number(it.quantity || 0);
      return qty > 1 ? `${qty}× ${name}` : name;
    });
    if (parts.length <= 2) return parts.join(", ");
    return `${parts.slice(0, 2).join(", ")} +${parts.length - 2}`;
  };

  return (
    <div className="space-y-8" data-testid="financeiro-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-tiny mb-2">Resultados</p>
          <h1 className="font-display text-4xl tracking-tight">Financeiro</h1>
          <p className="text-sm text-neutral-500 mt-1">Vendas + pedidos pagos + assistência consolidados.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <HideMoneyButton testId="financeiro-hide-money"/>
          <Button
            variant="outline"
            onClick={()=>setExportOpen(true)}
            data-testid="btn-export-financeiro"
          >
            <Download size={14} className="mr-1.5"/> Exportar relatório
          </Button>
          <Button
            onClick={()=>navigate("/financeiro/despesas")}
            className="bg-[#0A0A0A] hover:bg-black text-white dark:bg-white dark:text-[#0A0A0A] dark:hover:bg-neutral-200"
            data-testid="btn-despesas"
          >
            <TrendingDown size={14} className="mr-1.5"/> Despesas
          </Button>
        </div>
      </div>

      {/* Botões de preset */}
      <div className="flex flex-wrap gap-2" data-testid="fin-presets">
        {presetKeys.map(k => (
          <button
            key={k}
            type="button"
            onClick={() => k === "custom" ? setPreset("custom") : applyPreset(k)}
            className={`text-xs px-3 py-2 rounded-md border transition ${
              preset === k
                ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]"
                : "bg-white border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:border-white/10 dark:text-neutral-300"
            }`}
            data-testid={`fin-preset-${k}`}
          >
            {PRESETS[k].label}
          </button>
        ))}
      </div>

      {/* Filtro personalizado — só aparece quando preset === custom */}
      {preset === "custom" && (
        <Card className="p-6 border-[#E5E5E5] shadow-none" data-testid="fin-custom-filter">
          <div className="grid md:grid-cols-4 gap-4 items-end">
            <div>
              <Label>De</Label>
              <Input type="date" value={start} onChange={e=>setStart(e.target.value)} data-testid="fin-start-date"/>
            </div>
            <div>
              <Label>Até</Label>
              <Input type="date" value={end} onChange={e=>setEnd(e.target.value)} data-testid="fin-end-date"/>
            </div>
            <Button onClick={applyCustom} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="fin-filter-button">Filtrar</Button>
            <Button variant="outline" onClick={clearCustom} data-testid="fin-clear-button">Limpar filtro</Button>
          </div>
        </Card>
      )}

      {/* Filtro de Vendedor — só para ADM/Gerente/Master */}
      {canFilterSeller && (
        <Card className="p-4 border-[#E5E5E5] shadow-none" data-testid="fin-seller-filter">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-1.5 text-sm font-medium">
              <Filter size={14}/> Vendedor
            </div>
            <Select value={selectedSellers[0] || "all"} onValueChange={setSellerFilter}>
              <SelectTrigger className="w-56" data-testid="fin-seller-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os vendedores</SelectItem>
                {sellers.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedSellers.length > 0 && (
              <span className="text-xs text-neutral-500">Filtro ativo</span>
            )}
          </div>
        </Card>
      )}

      {/* Filtro de Lead (origem do cliente) — multi-seleção */}
      <Card className="p-4 border-[#E5E5E5] shadow-none" data-testid="fin-lead-filter">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="inline-flex items-center gap-1.5 text-sm font-medium">
            <Filter size={14}/>
            Filtrar por Lead
            <span className="text-xs text-neutral-500 font-normal">(origem do cliente)</span>
            {selectedLeads.length > 0 && (
              <span className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-[10px] px-1.5 py-0.5 rounded-full ml-1">
                {selectedLeads.length} selecionado{selectedLeads.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          {selectedLeads.length > 0 && (
            <button
              type="button"
              onClick={clearLeads}
              className="text-xs text-neutral-500 hover:text-red-600 underline"
              data-testid="fin-lead-clear"
            >
              Limpar (mostrar todos)
            </button>
          )}
        </div>
        {leads.length === 0 ? (
          <p className="text-xs text-neutral-400 italic">Nenhum Lead cadastrado. Crie no PDV ao realizar uma venda.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {leads.map(l => {
              const on = selectedLeads.includes(l.id);
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => toggleLead(l.id)}
                  data-testid={`fin-lead-chip-${l.id}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition ${on
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white dark:bg-zinc-900 text-neutral-700 dark:text-neutral-300 border-[#E5E5E5] dark:border-white/10 hover:border-purple-400"
                    }`}
                >
                  {l.name}
                </button>
              );
            })}
          </div>
        )}
        {selectedLeads.length === 0 && (
          <p className="text-[10px] text-neutral-400 italic mt-2">Nenhum selecionado = mostrar Todos os Leads</p>
        )}
      </Card>

      {/* Filtro de categorias de produtos — multi-seleção */}
      <Card className="p-4 border-[#E5E5E5] shadow-none" data-testid="fin-category-filter">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <button
            type="button"
            onClick={() => setCatFilterOpen(o => !o)}
            className="inline-flex items-center gap-1.5 text-sm font-medium"
            data-testid="fin-category-toggle"
          >
            <Filter size={14}/>
            Filtrar por categorias
            {selectedCats.length > 0 && (
              <span className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[10px] px-1.5 py-0.5 rounded-full ml-1">
                {selectedCats.length} selecionada{selectedCats.length !== 1 ? "s" : ""}
              </span>
            )}
          </button>
          {selectedCats.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {selectedCats.slice(0, 5).map(cid => {
                const c = categories.find(x => x.id === cid);
                if (!c) return null;
                return (
                  <span key={cid} className="inline-flex items-center gap-1 text-[10px] bg-neutral-100 dark:bg-white/10 rounded-full px-2 py-0.5">
                    {c.name}
                    <button type="button" onClick={() => toggleCategory(cid)} className="hover:text-red-500"><XIcon size={10}/></button>
                  </span>
                );
              })}
              {selectedCats.length > 5 && <span className="text-[10px] text-neutral-500">+{selectedCats.length - 5}</span>}
              <button type="button" onClick={clearCategoryFilter} className="text-[10px] text-red-500 hover:underline ml-1" data-testid="fin-category-clear">Limpar</button>
            </div>
          )}
        </div>
        {catFilterOpen && (
          <div className="border-t border-[#E5E5E5] dark:border-white/10 pt-3 mt-2">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <p className="text-[11px] text-neutral-500">Marque uma ou mais categorias. Faturamento, custo e lucro consideram apenas itens dessas categorias. <span className="font-medium">Assistência não entra com filtro de categoria.</span></p>
              <div className="flex gap-1.5">
                <button type="button" onClick={selectAllCategories} className="text-[11px] px-2 py-1 rounded border border-[#E5E5E5] dark:border-white/10 hover:bg-neutral-50 dark:hover:bg-white/5" data-testid="fin-category-select-all">Selecionar todas</button>
                <button type="button" onClick={clearCategoryFilter} className="text-[11px] px-2 py-1 rounded border border-[#E5E5E5] dark:border-white/10 hover:bg-neutral-50 dark:hover:bg-white/5">Limpar</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {categories.length === 0 && (
                <span className="text-[11px] text-neutral-400 italic">Nenhuma categoria cadastrada.</span>
              )}
              {categories.map(c => {
                const active = selectedCats.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCategory(c.id)}
                    className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition ${
                      active
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white dark:bg-transparent border-[#E5E5E5] dark:border-white/10 hover:border-emerald-400"
                    }`}
                    data-testid={`fin-category-chip-${c.id}`}
                  >
                    {active && <CheckIcon size={10}/>}
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <div className="grid md:grid-cols-3 gap-6">
        <Card className="p-6 border-[#E5E5E5] shadow-none">
          <p className="label-tiny">Total faturado</p>
          <div className="font-display text-3xl mt-2"><Money value={data.total_sold}/></div>
        </Card>
        <Card className="p-6 border-[#E5E5E5] shadow-none">
          <p className="label-tiny">Total de custos</p>
          <div className="font-display text-3xl mt-2"><Money value={data.total_cost}/></div>
        </Card>
        <Card className="p-6 border-[#E5E5E5] shadow-none bg-[#0A0A0A] text-white">
          <p className="label-tiny text-white/60">Lucro líquido</p>
          <div className="font-display text-3xl mt-2"><Money value={data.net_profit}/></div>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Card className="p-6 border-[#E5E5E5] shadow-none">
          <div className="flex items-center gap-2 mb-2"><ShoppingCart size={16} className="text-neutral-500"/><p className="label-tiny">Vendas</p></div>
          <div className="space-y-1">
            <div className="flex justify-between text-sm"><span className="text-neutral-500">Faturado</span><span><Money value={data.sales_total}/></span></div>
            <div className="flex justify-between font-display text-xl"><span>Lucro</span><span className="text-green-700"><Money value={data.sales_profit}/></span></div>
          </div>
        </Card>
        <Card className="p-6 border-[#E5E5E5] shadow-none">
          <div className="flex items-center gap-2 mb-2"><Package size={16} className="text-neutral-500"/><p className="label-tiny">Pedidos pagos</p></div>
          <div className="space-y-1">
            <div className="flex justify-between text-sm"><span className="text-neutral-500">Faturado</span><span><Money value={data.paid_total}/></span></div>
            <div className="flex justify-between font-display text-xl"><span>Lucro</span><span className="text-green-700"><Money value={data.paid_profit}/></span></div>
          </div>
        </Card>
        <Card className="p-6 border-[#E5E5E5] shadow-none">
          <div className="flex items-center gap-2 mb-2"><Wrench size={16} className="text-neutral-500"/><p className="label-tiny">Assistência</p></div>
          <div className="space-y-1">
            <div className="flex justify-between text-sm"><span className="text-neutral-500">Faturado</span><span><Money value={data.service_total}/></span></div>
            <div className="flex justify-between font-display text-xl"><span>Lucro</span><span className="text-green-700"><Money value={data.service_profit}/></span></div>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="sales">
        <TabsList>
          <TabsTrigger value="sales" data-testid="tab-vendas">Vendas ({data.sales.length})</TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-pedidos-pagos">Pedidos pagos ({(data.paid_orders || []).length})</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-assistencia">Assistência ({data.services.length})</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="tab-comissoes-pagas">Comissões pagas ({(data.commissions_paid || []).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="mt-4">
          <Card className="border-[#E5E5E5] shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-[#E5E5E5] text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Nº</th>
                  <th className="px-4 py-3 font-medium">Data</th>
                  <th className="px-4 py-3 font-medium">Produto</th>
                  <th className="px-4 py-3 font-medium">Pagamento</th>
                  <th className="px-4 py-3 font-medium">Vendedor</th>
                  <th className="px-4 py-3 font-medium text-right">Valor</th>
                  <th className="px-4 py-3 font-medium text-right">Lucro</th>
                </tr>
              </thead>
              <tbody>
                {data.sales.map(s => (
                  <tr key={s.id} className="border-b border-[#E5E5E5]">
                    <td className="px-4 py-3 font-mono text-xs">#{String(s.order_number || "").padStart(6,'0')}</td>
                    <td className="px-4 py-3 text-xs">{new Date(s.created_at).toLocaleString('pt-BR')}</td>
                    <td className="px-4 py-3 text-xs text-neutral-700 max-w-[260px] truncate" title={summarizeItems(s.items)}>{summarizeItems(s.items)}</td>
                    <td className="px-4 py-3">{s.payment_method === "Pendente programado" ? "Crediário" : s.payment_method}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{sellerLabel(s)}</td>
                    <td className="px-4 py-3 text-right"><Money value={s.total}/></td>
                    <td className="px-4 py-3 text-right text-green-700"><Money value={s.profit}/></td>
                  </tr>
                ))}
                {data.sales.length === 0 && <tr><td colSpan="7" className="text-center py-12 text-neutral-500 text-sm">Sem vendas no período.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="paid" className="mt-4">
          <Card className="border-[#E5E5E5] shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-[#E5E5E5] text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Nº</th>
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">Data</th>
                  <th className="px-4 py-3 font-medium">Produto</th>
                  <th className="px-4 py-3 font-medium">Pagamento</th>
                  <th className="px-4 py-3 font-medium">Vendedor</th>
                  <th className="px-4 py-3 font-medium text-right">Valor</th>
                  <th className="px-4 py-3 font-medium text-right">Lucro</th>
                </tr>
              </thead>
              <tbody>
                {(data.paid_orders || []).map(p => (
                  <tr key={p.id} className="border-b border-[#E5E5E5]">
                    <td className="px-4 py-3 font-mono text-xs">#{String(p.order_number || "").padStart(6,'0')}</td>
                    <td className="px-4 py-3 text-xs truncate max-w-[200px]">{p.customer_name || "—"}</td>
                    <td className="px-4 py-3 text-xs">{new Date(p.created_at).toLocaleString('pt-BR')}</td>
                    <td className="px-4 py-3 text-xs text-neutral-700 max-w-[260px] truncate" title={summarizeItems(p.items)}>{summarizeItems(p.items)}</td>
                    <td className="px-4 py-3">{p.payment_method === "Pendente programado" ? "Crediário" : p.payment_method}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{sellerLabel(p)}</td>
                    <td className="px-4 py-3 text-right"><Money value={p.total}/></td>
                    <td className="px-4 py-3 text-right text-green-700"><Money value={p.profit}/></td>
                  </tr>
                ))}
                {(data.paid_orders || []).length === 0 && <tr><td colSpan="8" className="text-center py-12 text-neutral-500 text-sm">Sem pedidos pagos no período.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="services" className="mt-4">
          <Card className="border-[#E5E5E5] shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-[#E5E5E5] text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">OS</th>
                  <th className="px-4 py-3 font-medium">Produto</th>
                  <th className="px-4 py-3 font-medium">Data</th>
                  <th className="px-4 py-3 font-medium text-right">Valor</th>
                  <th className="px-4 py-3 font-medium text-right">Lucro</th>
                </tr>
              </thead>
              <tbody>
                {data.services.map(s => (
                  <tr key={s.id} className="border-b border-[#E5E5E5]">
                    <td className="px-4 py-3 font-mono text-xs">#{String(s.os_number || "").padStart(6,'0')}</td>
                    <td className="px-4 py-3 truncate max-w-[200px]">{s.product_name}</td>
                    <td className="px-4 py-3 text-xs">{new Date(s.created_at).toLocaleString('pt-BR')}</td>
                    <td className="px-4 py-3 text-right"><Money value={s.total}/></td>
                    <td className="px-4 py-3 text-right text-green-700"><Money value={s.profit}/></td>
                  </tr>
                ))}
                {data.services.length === 0 && <tr><td colSpan="5" className="text-center py-12 text-neutral-500 text-sm">Sem OS finalizadas no período.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="commissions" className="mt-4">
          <Card className="border-[#E5E5E5] shadow-none overflow-hidden">
            {(data.commissions_paid || []).length > 0 && (
              <div className="px-4 py-3 border-b border-[#E5E5E5] bg-amber-50 dark:bg-amber-500/10 text-sm">
                Total de comissões pagas no período: <strong className="text-red-700 dark:text-red-300">−<Money value={data.commissions_paid_total || 0}/></strong>
                <span className="ml-2 text-neutral-500 text-xs">(descontado do lucro líquido)</span>
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-[#E5E5E5] text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Nº</th>
                  <th className="px-4 py-3 font-medium">Data pgto.</th>
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">Produtos</th>
                  <th className="px-4 py-3 font-medium">Vendedor</th>
                  <th className="px-4 py-3 font-medium text-right">Venda</th>
                  <th className="px-4 py-3 font-medium text-right">Comissão</th>
                </tr>
              </thead>
              <tbody>
                {(data.commissions_paid || []).map(c => (
                  <tr key={c.id} className="border-b border-[#E5E5E5]" data-testid={`fin-commission-${c.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">#{String(c.order_number || "").padStart(6,"0")}</td>
                    <td className="px-4 py-3 text-xs">{c.paid_at ? new Date(c.paid_at).toLocaleDateString('pt-BR') : "—"}</td>
                    <td className="px-4 py-3 text-xs">{c.customer_name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-neutral-700 dark:text-neutral-300 max-w-[240px] truncate" title={(c.items||[]).map(it=>`${it.quantity>1?it.quantity+'× ':''}${it.name}`).join(", ")}>
                      {(c.items||[]).slice(0,2).map(it => it.quantity>1?`${it.quantity}× ${it.name}`:it.name).join(", ")}
                      {(c.items||[]).length > 2 && ` +${c.items.length-2}`}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{c.created_by}</td>
                    <td className="px-4 py-3 text-right text-neutral-500 text-xs"><Money value={c.sale_total}/></td>
                    <td className="px-4 py-3 text-right font-medium text-red-600"><Money value={c.amount}/></td>
                  </tr>
                ))}
                {(data.commissions_paid || []).length === 0 && (
                  <tr><td colSpan="7" className="text-center py-12 text-neutral-500 text-sm">Sem comissões pagas no período.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
      <FinanceiroExportDialog open={exportOpen} onClose={()=>setExportOpen(false)} categories={categories}/>
    </div>
  );
}
